import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import type { UserProfile, Inventory, EvidenceLog } from '@/types/database'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { StreakModal } from '@/components/streak/StreakModal'
import { Skeleton } from '@/components/ui/skeleton'
import { PremiumBadge } from '@/components/subscription/PremiumBadge'
import { useCelebrationAnimationActions } from '@/components/animations/CelebrationAnimationContext'
import { useAdmin } from '@/hooks/useAdmin'
import { Shield } from 'lucide-react'
import { track } from '@/lib/analytics'

interface TopBarProps {
  profile: UserProfile | null
  inventory: Inventory[]
  globalStreak?: number
  yearlyLogs?: EvidenceLog[]
  avatarUrl?: string | null
  isLoading?: boolean
}

export function TopBar({ profile, inventory, globalStreak = 0, yearlyLogs = [], avatarUrl = null, isLoading = false }: TopBarProps) {
  const [showStreakModal, setShowStreakModal] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false)
  const { isAdmin } = useAdmin()
  
  const { registerShardsCounter, registerStreakCounter, setTopBarVisible } = useCelebrationAnimationActions()
  
  const shields = inventory.find(i => i.item_type === 'streak_shield')?.quantity || 0
  const restTickets = inventory.find(i => i.item_type === 'rest_ticket')?.quantity || 0

  const avatarInitials = (() => {
    const name = profile?.display_name || profile?.username || ''
    const parts = name.trim().split(/\s+/)
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : (name[0] || '?').toUpperCase()
  })()
  
  // Callback refs for registering counter elements
  const shardsRef = useCallback((node: HTMLSpanElement | null) => {
    registerShardsCounter(node)
  }, [registerShardsCounter])
  
  const streakRef = useCallback((node: HTMLSpanElement | null) => {
    registerStreakCounter(node)
  }, [registerStreakCounter])
  
  // Reset avatar load failure when avatarUrl changes (e.g. session refresh)
  useEffect(() => {
    setAvatarLoadFailed(false)
  }, [avatarUrl])

  // Scroll detection
  useEffect(() => {
    let lastScrollY = window.scrollY
    let ticking = false
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY
          const wasScrolled = lastScrollY > 50
          const nowScrolled = currentScrollY > 50
          
          if (wasScrolled !== nowScrolled) {
            setIsScrolled(nowScrolled)
            // Report wallet visibility to animation context
            // Wallet is visible when NOT scrolled (bottom row shows)
            setTopBarVisible(!nowScrolled)
          }
          
          lastScrollY = currentScrollY
          ticking = false
        })
        ticking = true
      }
    }
    
    const initialScrolled = window.scrollY > 50
    setIsScrolled(initialScrolled)
    setTopBarVisible(!initialScrolled)
    
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [setTopBarVisible])

  return (
    <>
      <header 
        className="fixed top-0 left-0 right-0 z-40 lg:left-16 pointer-events-none"
      >
        {/* Animated background - fades in on scroll */}
        <div 
          className={`absolute inset-0 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl transition-opacity duration-300 ease-out ${
            isScrolled ? 'opacity-100' : 'opacity-0'
          }`}
        />
        
        {/* Safe area spacer - extends background under status bar */}
        <div className="relative" style={{ height: 'var(--native-safe-area-inset-top, env(safe-area-inset-top, 0px))' }} />
        <div className="relative px-4 py-3">
          {/* Top Row: Avatar + Name | Fire/Streak */}
          <div className="flex items-center justify-between">
            {/* Left: User Avatar + Name */}
            <div className="flex items-center gap-2 pointer-events-auto">
              {/* Avatar - clickable to go to profile */}
              <Link to="/profile" onClick={() => track('topbar_profile_clicked', {}, 'navigation')}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative w-8 h-8 rounded-full overflow-hidden shadow-xl ring-2 ring-white/20 dark:ring-black/20 hover:ring-emerald-500/50 transition-all"
                >
                  {profile?.profile_emoji ? (
                    // User-selected emoji avatar
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-100 to-cyan-100 dark:from-emerald-900/30 dark:to-cyan-900/30">
                      <FluentEmoji emoji={profile.profile_emoji} size={24} animated />
                    </div>
                  ) : avatarUrl && !avatarLoadFailed ? (
                    // Google profile picture - referrerPolicy fixes 403 from Google's image servers
                    <img
                      src={avatarUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={() => setAvatarLoadFailed(true)}
                    />
                  ) : (
                    // Fallback: user initials
                    <div className="w-full h-full flex items-center justify-center bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold">
                      {avatarInitials}
                    </div>
                  )}
                </motion.div>
              </Link>

              {/* User handle - clickable to profile */}
              <Link to="/profile" className="flex items-center gap-1.5">
                <span className="font-semibold text-slate-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                  {profile?.username ? `@${profile.username}` : 'Miac'}
                </span>
                {profile?.subscription_tier === 'premium' && <PremiumBadge size="xs" variant="shimmer" />}
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-900/60 transition-colors"
                >
                  <Shield size={12} />
                  <span className="text-[10px] font-bold uppercase tracking-wider">Admin</span>
                </Link>
              )}
            </div>
            
            {/* Right: Streak (clickable) + Notification Bell */}
            <div className="flex items-center gap-2 pointer-events-auto">
              {/* Streak Button - clickable to open modal */}
              {isLoading ? (
                <Skeleton className="h-8 w-16 rounded-full" />
              ) : (
                <button
                  onClick={() => { track('topbar_streak_clicked', { global_streak: globalStreak }, 'navigation'); setShowStreakModal(true) }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all cursor-pointer hover:scale-105 active:scale-95 backdrop-blur-md border ${
                    globalStreak > 0 
                      ? 'bg-orange-100/90 dark:bg-orange-900/50 hover:bg-orange-200 dark:hover:bg-orange-900/60 border-orange-200/50 dark:border-orange-800/30' 
                      : 'bg-slate-100/90 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 border-slate-200/50 dark:border-slate-700/30'
                  }`}
                >
                  <FluentEmoji emoji={globalStreak > 0 ? "🔥" : "🧊"} size={18} animated={globalStreak > 0} />
                  <span 
                    ref={streakRef}
                    className={`text-sm font-semibold tabular-nums ${
                      globalStreak > 0 
                        ? 'text-orange-600 dark:text-orange-400' 
                        : 'text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    {globalStreak}
                  </span>
                </button>
              )}
              
              {/* Notification Bell */}
              <NotificationBell 
                size={20}
                className="text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full"
              />
            </div>
          </div>

          {/* Bottom Row: Collapses on scroll */}
          <div 
            className={`overflow-hidden transition-all duration-300 ease-out ${
              isScrolled ? 'h-0 mt-0' : 'h-9 mt-1'
            }`}
          >
            <div 
              className={`flex items-center gap-1.5 pt-1 transition-all duration-300 ease-out ${
                isScrolled 
                  ? 'opacity-0 -translate-y-2 pointer-events-none' 
                  : 'opacity-100 translate-y-0 pointer-events-auto'
              }`}
            >
              {isLoading ? (
                <Skeleton className="h-7 w-32 rounded-full" />
              ) : (
                <Link
                  to="/shop"
                  onClick={() => track('topbar_wallet_clicked', { shards: profile?.currency_shards || 0, shields, rest_tickets: restTickets }, 'navigation')}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 h-7 rounded-full bg-slate-100/90 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors active:scale-95"
                >
                  {/* Shards */}
                  <FluentEmoji emoji="💎" size={14} />
                  <span 
                    ref={shardsRef}
                    className="text-xs font-semibold text-slate-900 dark:text-white tabular-nums leading-none"
                  >
                    {profile?.currency_shards || 0}
                  </span>
                  
                  <span className="text-slate-300 dark:text-slate-600">|</span>
                  
                  {/* Shields */}
                  <FluentEmoji emoji="🛡️" size={14} />
                  <span className={`text-xs font-semibold tabular-nums leading-none ${
                    shields > 0 ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-400'
                  }`}>
                    {shields}
                  </span>
                  
                  <span className="text-slate-300 dark:text-slate-600">|</span>
                  
                  {/* Rest Days */}
                  <FluentEmoji emoji="🏝️" size={14} />
                  <span className={`text-xs font-semibold tabular-nums leading-none ${
                    restTickets > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'
                  }`}>
                    {restTickets}
                  </span>
                </Link>
              )}
            </div>
          </div>
        </div>
        
        {/* Bottom separator line - appears on scroll */}
        <div 
          className={`relative h-px bg-gradient-to-r from-transparent via-slate-300/60 dark:via-slate-600/50 to-transparent transition-opacity duration-300 ease-out ${
            isScrolled ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </header>
      
      <StreakModal
        open={showStreakModal}
        onClose={() => setShowStreakModal(false)}
        globalStreak={globalStreak}
        yearlyLogs={yearlyLogs}
      />
    </>
  )
}
